import dynamic from 'next/dynamic';
import { type FC, type ReactNode } from 'react';

import { useDeepEqualMemo } from '@craft/ui/hooks';

import { toClientAuthenticatedUser } from '@/src/auth/client-types';
import { type PageAuth } from '@/src/components-v2/types';
import { useAuthenticatedUser } from '@/src/hooks/query/users/useAuthenticatedUser';
import { useIsIdle } from '@/src/hooks/use-is-idle';
import { type PageAuthType } from '@/src/middleware/pages';

import { AuthenticatedUserProviderCore } from './AuthenticatedUserProviderCore';

const CraftLoadingScreen = dynamic(
  () => import('@/src/components-v2/brand/CraftLoadingScreen').then(mod => mod.CraftLoadingScreen),
  { ssr: false },
);

const PostLoginError = dynamic(
  () =>
    import('@/src/features/auth/components/errors/PostLoginError').then(mod => mod.PostLoginError),
  { ssr: false },
);

interface BootstrappedAuthenticatedUserProviderProps {
  readonly auth: PageAuth<PageAuthType.Auth0>;
  readonly children: ReactNode;
}

export const BootstrappedAuthenticatedUserProvider: FC<
  BootstrappedAuthenticatedUserProviderProps
> = ({ auth, children }) => {
  /**
   * The authenticated user is already bootstrapped from the server-provided prop, so the client
   * request to connect-graphql is not needed for the initial render. It is deferred until the
   * browser is idle to keep the initial load off the critical path; once it runs it stays active so
   * the query's {@link UserProfile} typename invalidation refreshes the user in context whenever
   * the user changes their own data during the session.
   */
  const isIdle = useIsIdle({ timeout: 5000 });
  const { error, isNotAuthenticated, user: clientUser } = useAuthenticatedUser({ pause: !isIdle });

  const bootstrappedUser = useDeepEqualMemo(
    () => toClientAuthenticatedUser(auth.user),
    [auth.user],
  );
  const resolvedUser = useDeepEqualMemo(
    () => clientUser ?? bootstrappedUser,
    [clientUser ?? bootstrappedUser],
  );

  if (error) {
    /**
     * A loading screen is only shown in the case that the error occurs while the client-side Urql
     * exchange, {@link AuthenticationRedirectExchange}, is actively in the process of forcing a
     * full page reload in order to automatically redirect the user back to the login page in cases
     * where the user's session has become stale or invalid since the time that the page was
     * rendered on the server with a valid session.
     *
     * In LTI contexts, this redirect does not occur (as of this point in time) because the
     * authentication does not rely on server-side session cookies in the same way that the Auth0
     * case does.  So, for LTI contexts, the error should be shown in the UI immediately because
     * it will not result in a "flash" of the error before the redirect occurs like it does in
     * the Auth0 case.
     */
    return isNotAuthenticated ? <CraftLoadingScreen /> : <PostLoginError />;
  }
  return (
    <AuthenticatedUserProviderCore auth={auth} user={resolvedUser}>
      {children}
    </AuthenticatedUserProviderCore>
  );
};
